
import React, { useState, useMemo, useCallback, useEffect } from 'react'
import {
  Modal, Input, Table, Button, Tag, Space, Typography,
  Divider, Badge, InputNumber, Empty, Card, Row, Col, Spin
} from 'antd'
import {
  SearchOutlined, PlusOutlined, MinusOutlined, DeleteOutlined, FireOutlined,
} from '@ant-design/icons'
import { getDrinks } from '../../../api/drinkApi'

const { Text } = Typography

// ─── Fetch drink data from API ────────────────────────────────────────────────
export const DRINK_ITEM = async () => {
  const req = await getDrinks()
  return req.data
}

export const DRINK_BOTTLE_ROW_CODE = 2
export const DRINK_PEG_ROW_BASE = 1000
export const isDrinkBillRow = (item) => Number(item?.is_parcel || 0) >= DRINK_BOTTLE_ROW_CODE

// ─── helpers ─────────────────────────────────────────────────────────────────
const toNum = (value) => {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

const pickBottlePrice = (item, isACMode = false) => {
  const regular = toNum(item?.sell_price)
  const ac = toNum(item?.ac_price)
  return isACMode ? (ac > 0 ? ac : regular) : regular
}

const pickPegPrice = (peg, isACMode = false) => {
  const regular = toNum(peg?.price)
  const ac = toNum(peg?.ac_price)
  return isACMode ? (ac > 0 ? ac : regular) : regular
}

const findDrinkExisting = (drinkItems, menuItemId) =>
  drinkItems.find((i) => String(i.menu_item_id) === String(menuItemId))

export const calcItemSubtotal = (item) => {
  const pegTotal = Object.entries(item.peg_qtys || {}).reduce((sum, [pegId, qty]) => {
    const peg = (item.pegs || []).find(p => String(p.id) === String(pegId))
    return sum + (qty || 0) * parseFloat(peg?.price || 0)
  }, 0)
  const bottleTotal = (item.bottle_qty || 0) * parseFloat(item.price_bottle || 0)
  return pegTotal + bottleTotal
}

// export const addDrinkItemToList = (drinkItems, menuItem) => {
//   if (findDrinkExisting(drinkItems, menuItem.id)) return drinkItems

//   return [
//     ...drinkItems,
//     {
//       menu_item_id: menuItem.id,
//       name: menuItem.name,
//       price_bottle: menuItem.sell_price,
//       pegs: menuItem.pegs || [],
//       peg_qtys: {},
//       bottle_qty: 1,
//       cgst_pct: menuItem.cgst ? parseFloat(menuItem.cgst) : 0,
//       sgst_pct: menuItem.sgst ? parseFloat(menuItem.sgst) : 0,
//       category_name: menuItem.category_name,
//     },
//   ]
// }

export const addDrinkItemToList = (drinkItems, menuItem,isACMode) => {
  if (findDrinkExisting(drinkItems, menuItem.id)) return drinkItems

  const isDrink = Number(menuItem.is_drink) === 1

  return [
    ...drinkItems,
    {
      menu_item_id: menuItem.id,
      name: menuItem.name,
      price_bottle: pickBottlePrice(menuItem, isACMode),
      pegs: (menuItem.pegs || []).map((peg) => ({
        ...peg,
        price: pickPegPrice(peg, isACMode),
      })),
      peg_qtys: {},
      bottle_qty: 0,

      // ✅ DEFAULT 0 FOR DRINKS
      cgst_pct: isDrink
        ? Number(menuItem.cgst || 0)
        : (menuItem.cgst ? parseFloat(menuItem.cgst) : 0),

      sgst_pct: isDrink
        ? Number(menuItem.sgst || 0)
        : (menuItem.sgst ? parseFloat(menuItem.sgst) : 0),

      category_name: menuItem.category_name,
      is_drink: menuItem.is_drink,
    },
  ]
}

export const changeDrinkPegInList = (drinkItems, menuItemId, pegId, newQty) =>
  drinkItems
    .map(i => {
      if (String(i.menu_item_id) !== String(menuItemId)) return i
      return { ...i, peg_qtys: { ...i.peg_qtys, [pegId]: Math.max(0, newQty) } }
    })
    .filter(i => {
      const totalPegs = Object.values(i.peg_qtys || {}).reduce((s, q) => s + q, 0)
      return totalPegs > 0 || (i.bottle_qty || 0) > 0
    })

export const changeDrinkBottleInList = (drinkItems, menuItemId, newQty) =>
  drinkItems
    .map(i => String(i.menu_item_id) === String(menuItemId) ? { ...i, bottle_qty: Math.max(0, newQty) } : i)
    .filter(i => {
      const totalPegs = Object.values(i.peg_qtys || {}).reduce((s, q) => s + q, 0)
      return totalPegs > 0 || (i.bottle_qty || 0) > 0
    })

export const removeDrinkItemFromList = (drinkItems, menuItemId) =>
  drinkItems.filter(i => String(i.menu_item_id) !== String(menuItemId))

export const buildDrinkPayloadItems = (drinkItems = []) =>
  drinkItems.flatMap((item) => {
    const totalTaxPct = Number(item.cgst_pct || 0) + Number(item.sgst_pct || 0)
    const payload = []

    if (Number(item.bottle_qty || 0) > 0) {
      payload.push({
        menu_item_id: item.menu_item_id,
        name: `${item.name} (Bottle)`,
        price: Number(item.price_bottle || 0),
        qty: Number(item.bottle_qty || 0),
        tax_pct: totalTaxPct,
        is_parcel: DRINK_BOTTLE_ROW_CODE,
      })
    }

    Object.entries(item.peg_qtys || {}).forEach(([pegId, qty]) => {
      const pegQty = Number(qty || 0)
      const peg = (item.pegs || []).find((p) => String(p.id) === String(pegId))

      if (!peg || pegQty <= 0) return

      payload.push({
        menu_item_id: item.menu_item_id,
        name: `${item.name} (${peg.qty}ml Peg)`,
        price: Number(peg.price || 0),
        qty: pegQty,
        tax_pct: totalTaxPct,
        is_parcel: DRINK_PEG_ROW_BASE + Number(pegId),
      })
    })

    return payload
  })

export const mapBillItemsToDrinkItems = (billItems = [], drinkCatalog = []) => {
  const grouped = new Map()

  billItems
    .filter(isDrinkBillRow)
    .forEach((item) => {
      const menuItemId = item.menu_item_id
      const catalogDrink = (drinkCatalog || []).find(
        (drink) => String(drink.id) === String(menuItemId)
      )
      const existing = grouped.get(String(menuItemId)) || {
        menu_item_id: menuItemId,
        name: catalogDrink?.name || String(item.name || '').replace(/\s*\((Bottle|.*Peg)\)\s*$/i, ''),
        price_bottle: (catalogDrink?.ac_price ?? catalogDrink?.sell_price) || 0,
        pegs: catalogDrink?.pegs || [],
        peg_qtys: {},
        bottle_qty: 0,
        cgst_pct: catalogDrink?.cgst ? parseFloat(catalogDrink.cgst) : Number(item.tax_pct || 0) / 2,
        sgst_pct: catalogDrink?.sgst ? parseFloat(catalogDrink.sgst) : Number(item.tax_pct || 0) / 2,
        category_name: catalogDrink?.category_name || '',
      }

      if (Number(item.is_parcel) === DRINK_BOTTLE_ROW_CODE) {
        existing.bottle_qty = Number(item.qty || 0)
        existing.price_bottle = Number(item.price || existing.price_bottle || 0)
      } else if (Number(item.is_parcel) >= DRINK_PEG_ROW_BASE) {
        const pegId = Number(item.is_parcel) - DRINK_PEG_ROW_BASE
        const fallbackPeg = existing.pegs.find((peg) => String(peg.id) === String(pegId)) || {
          id: pegId,
          qty: '',
          price: Number(item.price || 0),
        }

        if (!existing.pegs.find((peg) => String(peg.id) === String(pegId))) {
          existing.pegs = [...existing.pegs, fallbackPeg]
        }

        existing.peg_qtys = {
          ...existing.peg_qtys,
          [pegId]: Number(item.qty || 0),
        }
      }

      grouped.set(String(menuItemId), existing)
    })

  return Array.from(grouped.values()).filter((item) => {
    const pegCount = Object.values(item.peg_qtys || {}).reduce((sum, qty) => sum + Number(qty || 0), 0)
    return pegCount > 0 || Number(item.bottle_qty || 0) > 0
  })
}

export const getDrinkTotals = (drinkItems = []) => {
  let subtotal = 0
  let cgst = 0
  let sgst = 0

  drinkItems.forEach(item => {
    const sub = calcItemSubtotal(item)
    subtotal += sub
    cgst += sub * (item.cgst_pct ? item.cgst_pct / 100 : 0)
    sgst += sub * (item.sgst_pct ? item.sgst_pct / 100 : 0)
  })

  return {
    drinkSubtotal: subtotal,
    drinkCgst: cgst,
    drinkSgst: sgst,
    drinkTotal: subtotal + cgst + sgst,
  }
}

const qBtnStyle = {
  width: 22, height: 22, borderRadius: '50%', border: '1px solid #d1d5db',
  background: '#f9fafb', cursor: 'pointer', display: 'inline-flex',
  alignItems: 'center', justifyContent: 'center', padding: 0,
}

const categoryColors = {
  Whisky:     { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' },
  Rum:        { bg: '#fce7f3', color: '#9d174d', border: '#f9a8d4' },
  Vodka:      { bg: '#ede9fe', color: '#5b21b6', border: '#c4b5fd' },
  Gin:        { bg: '#d1fae5', color: '#065f46', border: '#6ee7b7' },
  Wine:       { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' },
  'Red Wine': { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' },
  Beer:       { bg: '#fef9c3', color: '#713f12', border: '#fde047' },
}

// ─── DrinkCard ────────────────────────────────────────────────────────────────
// ─── DrinkCard ────────────────────────────────────────────────────────────────
const DrinkCard = ({ item, existing, onAdd, onChangePeg, onChangeBottle, isACMode = false }) => {
console.log("DRINK ITEM:", item)


  const hasPegs = item.pegs && item.pegs.length > 0
  const pegList = existing?.pegs?.length ? existing.pegs : (item.pegs || [])
  const bottleQty = existing?.bottle_qty || 0
  const cc = categoryColors[item.category_name] || { bg: '#f3f4f6', color: '#374151', border: '#d1d5db' }

  // ✅ Auto-add item to drinkItems before applying qty change
  const handlePegChange = (itemId, pegId, newQty) => {
    if (!existing)  {
      onAdd(item)
      onChangePeg(itemId, pegId, Math.max(0, newQty))
      return
    }

    onChangePeg(itemId, pegId, newQty)
  }

  const handleBottleChange = (itemId, newQty) => {
    if (!existing) {
      onAdd(item)
      onChangeBottle(itemId, Math.max(0, newQty))
      return
    }
    onChangeBottle(itemId, newQty)
  }


 const bottlePrice = existing?.price_bottle ?? pickBottlePrice(item, isACMode)

  return (
    <div
      onClick={() => !existing && onAdd(item)}
      style={{
        border: existing ? '1.5px solid #f59e0b' : '1px solid #e5e7eb',
        borderRadius: 10,
        padding: '12px 14px',
        background: existing ? '#fffbeb' : '#fff',
        cursor: existing ? 'default' : 'pointer',
        transition: 'all 0.15s',
        position: 'relative',
        minHeight: 140,
      }}
    >
      {/* category badge */}
      <div style={{ position: 'absolute', top: 10, right: 10 }}>
        <span style={{
          fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
          background: cc.bg, color: cc.color, border: `1px solid ${cc.border}`,
        }}>{item.category_name}</span>
      </div>

      {/* name + volume */}
      <div style={{ paddingRight: 70 }}>
        <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.3, color: '#1f2937' }}>{item.name}</div>
        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{item.qty} {item.unit}</div>
      </div>

      {/* bottle price */}
      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: 10, color: '#9ca3af' }}>Full bottle ({item.qty} {item.unit})</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#d97706' }}> ₹{parseFloat(bottlePrice).toFixed(2)}</div>
      </div>

      {/* peg options shown only when NOT yet added */}
      {hasPegs && !existing && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 4 }}>Peg options</div>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {pegList.map(peg => (
              <span key={peg.id} style={{
                fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 12,
                background: '#ede9fe', color: '#5b21b6', border: '1px solid #c4b5fd',
              }}>
                {peg.qty}ml — ₹{parseFloat(pickPegPrice(peg, isACMode)).toFixed(2)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* qty controls — only shown when item is added OR to trigger add-on-first-touch */}
      <div
        style={{ marginTop: 10, borderTop: '1px solid #fde68a', paddingTop: 10 }}
        onClick={e => e.stopPropagation()}
      >
        {/* Per-peg controls */}
        {hasPegs && pegList.map(peg => {
          const pegQty = existing?.peg_qtys?.[peg.id] || 0
          return (
            <div key={peg.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <span style={{ fontSize: 11, color: '#6b7280', minWidth: 64 }}>{peg.qty}ml peg</span>
              <button style={qBtnStyle} onClick={() => handlePegChange(item.id, peg.id, Math.max(0, pegQty - 1))}>
                <MinusOutlined style={{ fontSize: 8 }} />
              </button>
              <span style={{ fontSize: 13, fontWeight: 600, minWidth: 20, textAlign: 'center' }}>{pegQty}</span>
              <button style={qBtnStyle} onClick={() => handlePegChange(item.id, peg.id, pegQty + 1)}>
                <PlusOutlined style={{ fontSize: 8 }} />
              </button>
              <span style={{ fontSize: 10, color: '#9ca3af' }}>× ₹{parseFloat(existing ? peg.price : pickPegPrice(peg, isACMode)).toFixed(2)}</span>
            </div>
          )
        })}

        {/* Bottle control */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, color: '#6b7280', minWidth: 64 }}>Bottle</span>
          <button style={qBtnStyle} onClick={() => handleBottleChange(item.id, Math.max(0, bottleQty - 1))}>
            <MinusOutlined style={{ fontSize: 8 }} />
          </button>
          <span style={{ fontSize: 13, fontWeight: 600, minWidth: 20, textAlign: 'center' }}>{bottleQty}</span>
          <button style={qBtnStyle} onClick={() => handleBottleChange(item.id, bottleQty + 1)}>
            <PlusOutlined style={{ fontSize: 8 }} />
          </button>
          <span style={{ fontSize: 10, color: '#9ca3af' }}>× ₹{parseFloat(bottlePrice).toFixed(2)}</span>
        </div>
      </div>
    </div>
  )
}
// ─── DrinkPickerModal ─────────────────────────────────────────────────────────
export const DrinkPickerModal = ({ open, onClose, drinkItems, onAdd, onChangePeg, onChangeBottle, isACMode, isAcTable }) => {
  const acMode = useMemo(
    () => (typeof isACMode === 'boolean' ? isACMode : !!isAcTable),
    [isACMode, isAcTable]
  )
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('All')
  const [apiDrinks, setApiDrinks] = useState([])
  const [loading, setLoading] = useState(false)

  // Derive categories dynamically from API data
  const categories = useMemo(() =>
    ['All', ...new Set(apiDrinks.map(d => d.category_name).filter(Boolean))],
    [apiDrinks]
  )

  useEffect(() => {
    if (open && apiDrinks.length === 0) {
      setLoading(true)
      DRINK_ITEM()
        .then(data => setApiDrinks(data || []))
        .catch(err => console.error('Failed to load drinks:', err))
        .finally(() => setLoading(false))
    }
  }, [open])

  const filtered = useMemo(() =>
    apiDrinks.filter(m => {
      const matchCat = category === 'All' || m.category_name === category
      const matchSearch = m.name.toLowerCase().includes(search.toLowerCase())
      return matchCat && matchSearch
    }),
    [search, category, apiDrinks]
  )

  return (
    <Modal
      title={
        <Space>
          <span>🍷 Wines & Spirits</span>
          <Tag color="orange" style={{ fontSize: 11 }}>CGST + SGST applicable</Tag>
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={1000}
      styles={{ body: { padding: '16px 24px 24px' } }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        <Input
          prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
          placeholder="Search wines, spirits, beer…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          allowClear
          size="large"
        />

        {/* dynamic category chips */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {categories.map(c => (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              style={{
                padding: '4px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 500,
                background: category === c ? '#d97706' : '#f3f4f6',
                color: category === c ? '#fff' : '#6b7280',
                transition: 'all 0.15s',
              }}
            >{c}</button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" tip="Loading drinks…" />
          </div>
        ) : filtered.length === 0 ? (
          <Empty description="No items found" />
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 10, maxHeight: 420, overflowY: 'auto', paddingRight: 4,
          }}>
            {filtered.map(item => {
              const existing = findDrinkExisting(drinkItems, item.id)
              return (
                <DrinkCard
                  key={item.id}
                  item={item}
                  existing={existing}
                  onAdd={(drink) => onAdd(drink, acMode)}
                  onChangePeg={onChangePeg}
                  onChangeBottle={onChangeBottle}
                  isACMode={acMode}
                />
              )
            })}
          </div>
        )}
      </Space>
    </Modal>
  )
}

// ─── DrinkBillSection ─────────────────────────────────────────────────────────
export const DrinkBillSection = ({ drinkItems, onChangePeg, onChangeBottle, onRemove }) => {
  const columns = [
    {
      title: 'Item',
      dataIndex: 'name',
      key: 'name',
      render: (name, rec) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{name}</div>
          <div style={{ fontSize: 11, color: '#9ca3af' }}>{rec.category_name}</div>
        </div>
      ),
    },
    {
      title: 'Bottle Price',
      key: 'price',
      width: 120,
      render: (_, rec) => (
        <div style={{ fontSize: 12, color: '#d97706', fontWeight: 500 }}>
          ₹{parseFloat(rec.price_bottle).toFixed(2)}
          <span style={{ color: '#9ca3af', fontSize: 10, fontWeight: 400 }}> /bottle</span>
        </div>
      ),
    },
    {
      title: 'Pegs',
      key: 'pegs',
      width: 240,
      render: (_, rec) => {
        if (!rec.pegs || rec.pegs.length === 0)
          return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {rec.pegs.map(peg => {
              const pegQty = rec.peg_qtys?.[peg.id] || 0
              return (
                <div key={peg.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 11, color: '#6b7280', minWidth: 52 }}>{peg.qty}ml</span>
                  <button style={qBtnStyle} type="button"
                    onClick={() => onChangePeg(rec.menu_item_id, peg.id, Math.max(0, pegQty - 1))}>
                    <MinusOutlined style={{ fontSize: 8 }} />
                  </button>
                  <InputNumber
                    min={0} max={99} value={pegQty} controls={false}
                    onChange={v => onChangePeg(rec.menu_item_id, peg.id, v || 0)}
                    size="small" style={{ width: 40 }}
                  />
                  <button style={qBtnStyle} type="button"
                    onClick={() => onChangePeg(rec.menu_item_id, peg.id, pegQty + 1)}>
                    <PlusOutlined style={{ fontSize: 8 }} />
                  </button>
                  <span style={{ fontSize: 10, color: '#9ca3af' }}>₹{parseFloat(peg.price).toFixed(2)}</span>
                </div>
              )
            })}
          </div>
        )
      },
    },
    {
      title: 'Bottles',
      key: 'bottles',
      width: 140,
      render: (_, rec) => {
        const bottleQty = rec.bottle_qty || 0
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button style={qBtnStyle} type="button"
              onClick={() => onChangeBottle(rec.menu_item_id, Math.max(0, bottleQty - 1))}>
              <MinusOutlined style={{ fontSize: 8 }} />
            </button>
            <InputNumber min={0} max={99} value={bottleQty} controls={false}
              onChange={v => onChangeBottle(rec.menu_item_id, v || 0)} size="small" style={{ width: 40 }} />
            <button style={qBtnStyle} type="button"
              onClick={() => onChangeBottle(rec.menu_item_id, bottleQty + 1)}>
              <PlusOutlined style={{ fontSize: 8 }} />
            </button>
          </div>
        )
      },
    },
    {
      title: 'CGST',
      key: 'cgst',
      width: 90,
      render: (_, rec) => {
        const sub = calcItemSubtotal(rec)
        const pct = rec.cgst_pct ? rec.cgst_pct / 100 : 0
        return <Text style={{ fontSize: 12 }}>₹{(sub * pct).toFixed(2)}</Text>
      },
    },
    {
      title: 'SGST',
      key: 'sgst',
      width: 90,
      render: (_, rec) => {
        const sub = calcItemSubtotal(rec)
        const pct = rec.sgst_pct ? rec.sgst_pct / 100 : 0
        return <Text style={{ fontSize: 12 }}>₹{(sub * pct).toFixed(2)}</Text>
      },
    },
    {
      title: 'Total',
      key: 'total',
      width: 110,
      render: (_, rec) => {
        const sub = calcItemSubtotal(rec)
        const cgstPct = rec.cgst_pct ? rec.cgst_pct / 100 : 0
        const sgstPct = rec.sgst_pct ? rec.sgst_pct / 100 : 0
        return <Text strong style={{ color: '#d97706' }}>₹{(sub * (1 + cgstPct + sgstPct)).toFixed(2)}</Text>
      },
    },
    {
      title: '',
      key: 'action',
      width: 40,
      render: (_, rec) => (
        <Button type="text" danger icon={<DeleteOutlined />} size="small"
          onClick={() => onRemove(rec.menu_item_id)} />
      ),
    },
  ]

  if (!drinkItems.length) return null

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
        background: 'linear-gradient(90deg, #fffbeb 0%, #fff 100%)',
        border: '1px solid #fde68a', borderRadius: '8px 8px 0 0', borderBottom: 'none',
      }}>
        <span style={{ fontSize: 16 }}>🍷</span>
        <span style={{ fontWeight: 600, fontSize: 13, color: '#92400e' }}>Wines & Spirits</span>
        <Badge count={drinkItems.length} style={{ backgroundColor: '#d97706' }} />
      </div>
      <Table
        dataSource={drinkItems}
        columns={columns}
        rowKey={r => r.menu_item_id}
        pagination={false}
        size="small"
        style={{ borderRadius: '0 0 8px 8px', overflow: 'hidden', border: '1px solid #fde68a', borderTop: 'none' }}
        scroll={{ x: 800 }}
      />
    </div>
  )
}

// ─── DrinkSummaryBlock ────────────────────────────────────────────────────────
export const DrinkSummaryBlock = ({ drinkItems }) => {
  const { drinkSubtotal, drinkCgst, drinkSgst, drinkTotal } = useMemo(
    () => getDrinkTotals(drinkItems),
    [drinkItems]
  )

  if (!drinkItems.length) return null

  return (
    <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '12px 14px', margin: '8px 0' }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#92400e', marginBottom: 8 }}>
        🍷 Wines & Spirits charges
      </div>
      <Row gutter={[0, 4]}>
        <Col span={24}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <Text type="secondary">Drinks subtotal</Text>
            <Text>₹{drinkSubtotal.toFixed(2)}</Text>
          </div>
        </Col>
        <Col span={24}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <Text type="secondary">CGST</Text>
            <Text>₹{drinkCgst.toFixed(2)}</Text>
          </div>
        </Col>
        <Col span={24}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <Text type="secondary">SGST</Text>
            <Text>₹{drinkSgst.toFixed(2)}</Text>
          </div>
        </Col>
        <Col span={24}>
          <Divider style={{ margin: '6px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <Text strong style={{ color: '#92400e' }}>Drinks total (incl. tax)</Text>
            <Text strong style={{ color: '#d97706' }}>₹{drinkTotal.toFixed(2)}</Text>
          </div>
        </Col>
      </Row>
    </div>
  )
}

// ─── useDrinkItems hook ───────────────────────────────────────────────────────
export const useDrinkItems = () => {
  const [drinkItems, setDrinkItems] = useState([])

  const addDrinkItem = useCallback((menuItem,isACMode=false) => {
    setDrinkItems(prev => addDrinkItemToList(prev, menuItem, isACMode))
  }, [])

  // changeDrinkPeg(menuItemId, pegId, newQty)
  const changeDrinkPeg = useCallback((menuItemId, pegId, newQty) => {
    setDrinkItems(prev => changeDrinkPegInList(prev, menuItemId, pegId, newQty))
  }, [])

  const changeDrinkBottle = useCallback((menuItemId, newQty) => {
    setDrinkItems(prev => changeDrinkBottleInList(prev, menuItemId, newQty))
  }, [])

  const removeDrinkItem = useCallback((menuItemId) => {
    setDrinkItems(prev => removeDrinkItemFromList(prev, menuItemId))
  }, [])

  const { drinkSubtotal, drinkCgst, drinkSgst, drinkTotal } = useMemo(
    () => getDrinkTotals(drinkItems),
    [drinkItems]
  )

  const toDrinkPayload = useCallback(() =>
    buildDrinkPayloadItems(drinkItems),
    [drinkItems]
  )

  return {
    drinkItems,
    setDrinkItems,
    addDrinkItem,
  
    changeDrinkPeg,
    changeDrinkBottle,
    removeDrinkItem,
    drinkSubtotal,
    drinkCgst,
    drinkSgst,
    drinkTotal,
    toDrinkPayload,
  }
}

// ─── Demo / Preview ───────────────────────────────────────────────────────────
const DrinkSectionDemo = () => {
  const [drinkModalOpen, setDrinkModalOpen] = useState(false)
   
  const isACMode = false


  const {
    drinkItems, addDrinkItem, changeDrinkPeg, changeDrinkBottle, removeDrinkItem, drinkTotal,
  } = useDrinkItems()

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <Button type="primary" icon={<PlusOutlined />} size="small">Add Items</Button>
        <Button
          icon={<span style={{ marginRight: 4 }}>🍷</span>}
          size="small"
          style={{ borderColor: '#f59e0b', color: '#92400e', background: '#fffbeb' }}
          onClick={() => setDrinkModalOpen(true)}
        >
          Add Drinks
        </Button>
        <Button size="small">Split Bill</Button>
        {drinkItems.length > 0 && (
          <Tag color="orange" style={{ display: 'flex', alignItems: 'center', height: 24 }}>
            🍷 {drinkItems.length} drink type(s) added
          </Tag>
        )}
      </div>

      <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', marginBottom: 0 }}>
        <div style={{ padding: '8px 12px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Food Items
        </div>
        <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
          Food items appear here — Add Items to populate
        </div>
      </div>

      <DrinkBillSection
        drinkItems={drinkItems}
        onChangePeg={changeDrinkPeg}
        onChangeBottle={changeDrinkBottle}
        onRemove={removeDrinkItem}
      />

      {drinkItems.length > 0 && (
        <Card style={{ marginTop: 16 }} size="small" title={<Text strong>Bill Summary</Text>}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 }}>
            <Text type="secondary">Food subtotal</Text><Text>₹0.00</Text>
          </div>
          <DrinkSummaryBlock drinkItems={drinkItems} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 13 }}>
            <Text type="secondary">Discount</Text><Text type="danger">- ₹0.00</Text>
          </div>
          <Divider style={{ margin: '8px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text strong style={{ fontSize: 16 }}>Total</Text>
            <Text strong style={{ fontSize: 18, color: '#f59e0b' }}>₹{drinkTotal.toFixed(2)}</Text>
          </div>
          <Button type="primary" block style={{ marginTop: 12, background: '#10b981' }} icon={<FireOutlined />}>
            Settle Bill — ₹{drinkTotal.toFixed(2)}
          </Button>
        </Card>
      )}

      <DrinkPickerModal
        open={drinkModalOpen}
        onClose={() => setDrinkModalOpen(false)}
        drinkItems={drinkItems}
        onAdd={(item)=>addDrinkItem(item,isACMode)}
        onChangePeg={changeDrinkPeg}
        onChangeBottle={changeDrinkBottle}
        isACMode={isACMode}
         
      />
    </div>
  )
}

export default DrinkSectionDemo
